/**
 * Typed client for the internal AEOS operations API.
 *
 * Every payload is parsed by a Zod schema before it reaches a component, so a shape
 * change upstream fails loudly here instead of rendering `undefined` as a fact. The
 * schemas mirror `aws-infra/lambda/src/services/aeos-control-service.ts` and the two
 * AEOS handlers; those files are the authority for field names and bounds.
 *
 * AEOS-sourced timestamps are parsed as plain strings on purpose: AEOS writes some of
 * them without a UTC offset, so `.datetime()` would reject live data. Change-request
 * timestamps are Peregrine's own and are validated as ISO instants.
 *
 * All calls go through `apiFetch`. `/ops/*` is Cognito-authorized and operator-gated;
 * `publicFetch` must never be used here.
 */
import { z } from 'zod';

import { apiFetch } from './http';

/* ------------------------------------------------------------------ */
/* Enumerations mirrored from the backend                              */
/* ------------------------------------------------------------------ */

/** Statuses AEOS actually writes. The run feed filters on these, not on derived. */
export const AEOS_RUN_STATUSES = [
  'in_progress',
  'succeeded',
  'failed',
  'partial_success',
] as const;

/** Adds `abandoned`, which is Peregrine's reading of a stuck `in_progress` row. */
export const AEOS_DERIVED_RUN_STATUSES = [...AEOS_RUN_STATUSES, 'abandoned'] as const;

export const AEOS_RUN_ORIGINS = ['manual', 'scheduled'] as const;

export const AEOS_CHANGE_KINDS = [
  'module-pause',
  'invoice-policy',
  'account-policy',
  'account-managed',
] as const;

export const AEOS_CHANGE_SCOPES = ['global', 'account'] as const;

export const AEOS_CHANGE_REQUEST_STATUSES = ['pending-approval', 'approved'] as const;

export const AEOS_APPROVAL_REASONS = [
  'READY',
  'NOT_PENDING',
  'APPROVER_GROUP_REQUIRED',
  'SELF_APPROVAL_FORBIDDEN',
] as const;

/** AEOS `KNOWN_PHASES`, mirrored so an unbillable set cannot be submitted. */
export const AEOS_BILLABLE_PHASES = [
  'ACTIVELY_FUNDING',
  'CLOSED_LOST',
  'CLOSED_WON',
  'DEVELOPING',
  'LAUNCHED',
  'LEAD',
  'PROPOSED',
] as const;

/** AEOS `POLICY_BOUNDS`. The backend re-checks these and is the authority. */
export const AEOS_INVOICE_POLICY_BOUNDS = {
  overdueCriticalDays: { min: 1, max: 365 },
  unattendedGraceDays: { min: 0, max: 365 },
  materialGapCents: { min: 0, max: 100_000_000 },
} as const;

export const AEOS_REASON_MAX_LENGTH = 500;
export const AEOS_LIST_LIMIT_BOUNDS = { min: 1, max: 100 } as const;

const runStatusSchema = z.enum(AEOS_RUN_STATUSES);
const derivedRunStatusSchema = z.enum(AEOS_DERIVED_RUN_STATUSES);
const runOriginSchema = z.enum(AEOS_RUN_ORIGINS);
const changeKindSchema = z.enum(AEOS_CHANGE_KINDS);
const changeScopeSchema = z.enum(AEOS_CHANGE_SCOPES);
const changeStatusSchema = z.enum(AEOS_CHANGE_REQUEST_STATUSES);
const approvalReasonSchema = z.enum(AEOS_APPROVAL_REASONS);

const nonNegativeIntSchema = z.number().int().nonnegative();
const positiveIntSchema = z.number().int().positive();
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
const isoDateTimeSchema = z.string().datetime();

/* ------------------------------------------------------------------ */
/* Coverage — present on every read response                           */
/* ------------------------------------------------------------------ */

const coverageSchema = z.object({
  status: z.literal('partial'),
  represented: z.array(z.string()),
  notRepresented: z.array(z.string()),
});

/* ------------------------------------------------------------------ */
/* Estate                                                              */
/* ------------------------------------------------------------------ */

const runRowSchema = z.object({
  syncId: z.string(),
  integrationId: z.string(),
  partitionIntegrationId: z.string(),
  origin: runOriginSchema,
  syncTimestamp: z.string(),
  startedAt: z.string().optional(),
  completedAt: z.string().optional(),
  reportedStatus: runStatusSchema,
  derivedStatus: derivedRunStatusSchema,
  progressAvailable: z.boolean(),
  recordsProcessed: nonNegativeIntSchema.optional(),
  recordsFailed: nonNegativeIntSchema.optional(),
  durationMs: nonNegativeIntSchema.optional(),
  errorSummary: z.string().optional(),
  openForMs: nonNegativeIntSchema.optional(),
});

const logicalSyncSchema = z.object({
  integrationId: z.string(),
  partitionIntegrationId: z.string(),
  origin: runOriginSchema,
  correlation: z.literal('inferred'),
  windowStart: z.string(),
  windowEnd: z.string(),
  memberRunCount: nonNegativeIntSchema,
  memberSyncIds: z.array(z.string()),
  derivedStatus: derivedRunStatusSchema,
  progressAvailable: z.boolean(),
  recordsProcessed: nonNegativeIntSchema.optional(),
  recordsFailed: nonNegativeIntSchema.optional(),
});

/** Named for what moves them: connectivity tests bump these as well as syncs. */
const testTouchedCountersSchema = z.object({
  lastSyncAt: z.string().optional(),
  totalSyncs: nonNegativeIntSchema.optional(),
  successfulSyncs: nonNegativeIntSchema.optional(),
  failedSyncs: nonNegativeIntSchema.optional(),
  consecutiveFailures: nonNegativeIntSchema.optional(),
});

const runCountsSchema = z.object({
  total: nonNegativeIntSchema,
  succeeded: nonNegativeIntSchema,
  partialSuccess: nonNegativeIntSchema,
  failed: nonNegativeIntSchema,
  inProgress: nonNegativeIntSchema,
  abandoned: nonNegativeIntSchema,
});

const integrationRowSchema = z.object({
  integrationId: z.string(),
  integrationType: z.string(),
  status: z.string().optional(),
  authStatus: z.string().optional(),
  enabled: z.boolean().optional(),
  lastRunAt: z.string().optional(),
  lastSuccessfulRunAt: z.string().optional(),
  runCounts: runCountsSchema,
  runsExamined: nonNegativeIntSchema,
  runWindowLimit: nonNegativeIntSchema,
  testTouchedCounters: testTouchedCountersSchema,
  logicalSyncs: z.array(logicalSyncSchema),
  runs: z.array(runRowSchema),
  invalidItemCount: nonNegativeIntSchema,
});

const estateReportSchema = z.object({
  integrations: z.array(integrationRowSchema),
  invalidItemCount: nonNegativeIntSchema,
  coverage: coverageSchema,
});

const runFeedSchema = z.object({
  status: runStatusSchema,
  runs: z.array(runRowSchema),
  nextCursor: z.string().optional(),
  invalidItemCount: nonNegativeIntSchema,
  coverage: coverageSchema,
});

const runEntityRowSchema = z.object({
  syncId: z.string(),
  entityType: z.string(),
  entityId: z.string(),
  internalId: z.string().optional(),
  operation: z.string().optional(),
  status: z.string(),
  processedAt: z.string().optional(),
  errorMessage: z.string().optional(),
  errorCode: z.string().optional(),
});

const runEntityPageSchema = z.object({
  syncId: z.string(),
  entities: z.array(runEntityRowSchema),
  nextCursor: z.string().optional(),
  invalidItemCount: nonNegativeIntSchema,
  coverage: coverageSchema,
});

/* ------------------------------------------------------------------ */
/* Control plane                                                       */
/* ------------------------------------------------------------------ */

const moduleRowSchema = z.object({
  moduleId: z.string(),
  scope: z.literal('global'),
  enabled: z.boolean().optional(),
  pausedBy: z.string().optional(),
  pausedAt: z.string().optional(),
  pauseReason: z.string().optional(),
  settingsUpdatedAt: z.string().optional(),
  heartbeatPresent: z.boolean(),
  lastRanAt: z.string().optional(),
  lastRunOk: z.boolean().optional(),
  lastRunSkipped: z.boolean().optional(),
  lastRunSummary: z.string().optional(),
});

const invoicePolicyViewSchema = z.object({
  scope: z.literal('global'),
  present: z.boolean(),
  overdueCriticalDays: z.number().int().optional(),
  unattendedGraceDays: z.number().int().optional(),
  materialGapCents: z.number().int().optional(),
  /** AEOS stores free-form phase strings here, so this is not the phase enum. */
  billablePhases: z.array(z.string()).optional(),
  updatedAt: z.string().optional(),
});

const accountPolicyRowSchema = z.object({
  accountId: z.string(),
  scope: z.literal('account'),
  reconciliationExempt: z.boolean().optional(),
  exemptReason: z.string().optional(),
  materialGapCents: z.number().int().optional(),
  unattendedGraceDays: z.number().int().optional(),
  updatedAt: z.string().optional(),
});

const customerAccountRowSchema = z.object({
  accountId: z.string(),
  companyName: z.string().optional(),
  csManaged: z.boolean().optional(),
  prospectDocAccess: z.boolean().optional(),
  awsAccountIds: z.array(z.string()),
  policyPresent: z.boolean(),
});

const controlPlaneSummarySchema = z.object({
  modules: z.array(moduleRowSchema),
  invoicePolicy: invoicePolicyViewSchema,
  accountPolicies: z.array(accountPolicyRowSchema),
  customerAccounts: z.array(customerAccountRowSchema),
  globalScopeWarning: z.string(),
  invalidItemCount: nonNegativeIntSchema,
  coverage: coverageSchema,
});

/* ------------------------------------------------------------------ */
/* Unit economics — computed in AEOS, read here                        */
/* ------------------------------------------------------------------ */

const econRollUpRowSchema = z.object({
  scopeKey: z.string(),
  scope: z.string(),
  invocations: nonNegativeIntSchema,
  failed: nonNegativeIntSchema,
  units: nonNegativeIntSchema,
  unit: z.string().optional(),
  cost: z.number().nonnegative(),
  tokenCost: z.number().nonnegative(),
  resourceCost: z.number().nonnegative(),
  /** Absent when nothing was delivered OR the units are mixed. `mixedUnits` says which. */
  costPerUnit: z.number().nonnegative().optional(),
  mixedUnits: z.boolean(),
  /** Why this row's cost cannot be taken at face value. Empty when it can be. */
  qualifications: z.array(z.string()),
});

const econReportSchema = z.object({
  /** Null when AEOS has never computed a period. Not a period that cost nothing. */
  period: z.string().nullable(),
  computed: z.boolean(),
  computedAt: isoDateTimeSchema.optional(),
  attributionRevision: z.string().optional(),
  total: econRollUpRowSchema.nullable(),
  byApp: z.array(econRollUpRowSchema),
  byWorkflow: z.array(econRollUpRowSchema),
  invalidItemCount: nonNegativeIntSchema,
  coverage: coverageSchema,
});

/* ------------------------------------------------------------------ */
/* Change requests — the four kinds                                    */
/* ------------------------------------------------------------------ */

const moduleIdSchema = z.string().regex(/^[a-z][a-z0-9-]{1,63}$/, 'module id must be lowercase and hyphenated');
const accountIdSchema = z
  .string()
  .regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/, 'account id must be an alphanumeric identifier');
const reasonSchema = z
  .string()
  .trim()
  .min(1, 'a reason is required')
  .max(AEOS_REASON_MAX_LENGTH, `must be at most ${AEOS_REASON_MAX_LENGTH} characters`);

function boundedInt(bounds: { readonly min: number; readonly max: number }): z.ZodNumber {
  return z.number().int().min(bounds.min).max(bounds.max);
}

/** BLAST RADIUS: GLOBAL. One AEOS row governs every account. */
export const aeosModulePauseChangeSchema = z
  .object({
    kind: z.literal('module-pause'),
    moduleId: moduleIdSchema,
    paused: z.boolean(),
    reason: reasonSchema.optional(),
  })
  .strict();

/** BLAST RADIUS: GLOBAL. A single tenant-wide invoice policy row. */
export const aeosInvoicePolicyChangeSchema = z
  .object({
    kind: z.literal('invoice-policy'),
    overdueCriticalDays: boundedInt(AEOS_INVOICE_POLICY_BOUNDS.overdueCriticalDays),
    unattendedGraceDays: boundedInt(AEOS_INVOICE_POLICY_BOUNDS.unattendedGraceDays),
    materialGapCents: boundedInt(AEOS_INVOICE_POLICY_BOUNDS.materialGapCents),
    billablePhases: z
      .array(z.enum(AEOS_BILLABLE_PHASES))
      .min(1, 'at least one phase must be billable')
      .max(AEOS_BILLABLE_PHASES.length)
      .refine((phases) => new Set(phases).size === phases.length, 'phases must not be repeated'),
  })
  .strict();

export const aeosAccountPolicyChangeSchema = z
  .object({
    kind: z.literal('account-policy'),
    accountId: accountIdSchema,
    reconciliationExempt: z.boolean(),
    exemptReason: reasonSchema.optional(),
    materialGapCents: boundedInt(AEOS_INVOICE_POLICY_BOUNDS.materialGapCents).optional(),
    unattendedGraceDays: boundedInt(AEOS_INVOICE_POLICY_BOUNDS.unattendedGraceDays).optional(),
  })
  .strict();

export const aeosAccountManagedChangeSchema = z
  .object({
    kind: z.literal('account-managed'),
    accountId: accountIdSchema,
    csManaged: z.boolean(),
    prospectDocAccess: z.boolean().optional(),
  })
  .strict();

const desiredChangeVariantSchema = z.discriminatedUnion('kind', [
  aeosModulePauseChangeSchema,
  aeosInvoicePolicyChangeSchema,
  aeosAccountPolicyChangeSchema,
  aeosAccountManagedChangeSchema,
]);

type DesiredChangeVariant = z.infer<typeof desiredChangeVariantSchema>;

/**
 * Cross-field rules mirrored from the backend for form feedback only.
 *
 * Reads use the unrefined union: a stored record has already passed the backend's
 * own refinement, and re-litigating it in the browser would only risk blanking a
 * review view over a rule this file transcribed slightly differently.
 */
function validateDesiredChange(change: DesiredChangeVariant, context: z.RefinementCtx): void {
  if (change.kind === 'module-pause' && change.paused && change.reason === undefined) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['reason'],
      message: 'pausing a module stops it for every account and requires a reason',
    });
  }
  if (change.kind === 'account-policy') {
    if (change.reconciliationExempt && change.exemptReason === undefined) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['exemptReason'],
        message: 'an unexplained exemption is indistinguishable from hiding a problem',
      });
    }
    if (!change.reconciliationExempt && change.exemptReason !== undefined) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['exemptReason'],
        message: 'only an exemption may carry an exemption reason',
      });
    }
  }
}

export const aeosDesiredChangeInputSchema =
  desiredChangeVariantSchema.superRefine(validateDesiredChange);

export const aeosChangeRequestCreateSchema = z
  .object({
    idempotencyKey: z.string().uuid(),
    change: aeosDesiredChangeInputSchema,
  })
  .strict();

/* ------------------------------------------------------------------ */
/* Change requests — responses                                         */
/* ------------------------------------------------------------------ */

const approvalCapabilitySchema = z.object({
  allowed: z.boolean(),
  reason: approvalReasonSchema,
});

const changeSummarySchema = z.object({
  changeRequestId: z.string(),
  kind: changeKindSchema,
  scope: changeScopeSchema,
  status: changeStatusSchema,
  /** Moves only when the desired state moves; pinned 1:1 to desiredStateHash. */
  contentRevision: positiveIntSchema,
  /** Moves only on a lifecycle transition, so far only approval. */
  lifecycleRevision: positiveIntSchema,
  desiredStateHash: sha256Schema,
  /** Structural: Peregrine records intent and provisions nothing. */
  applied: z.literal(false),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
  approvedAt: isoDateTimeSchema.optional(),
});

const changeReviewSchema = changeSummarySchema.extend({
  desiredChange: desiredChangeVariantSchema,
  /** The exact bytes desiredStateHash covers. Shown, never summarized. */
  canonicalJson: z.string(),
  canonicalizationVersion: z.literal(1),
  createdBy: z.string(),
  approvedBy: z.string().optional(),
  approval: approvalCapabilitySchema,
});

const changeListResponseSchema = z.object({
  changeRequests: z.array(changeSummarySchema),
  nextCursor: z.string().optional(),
  applied: z.literal(false),
});

const changeDetailResponseSchema = z.object({
  changeRequest: changeReviewSchema,
  applied: z.literal(false),
});

const changeCreateResponseSchema = z.object({
  changeRequest: changeReviewSchema,
  idempotentReplay: z.boolean(),
  applied: z.literal(false),
});

const changeApprovalResponseSchema = z.object({
  changeRequest: changeReviewSchema,
  idempotentReplay: z.boolean(),
  applied: z.literal(false),
});

/* ------------------------------------------------------------------ */
/* Exported types                                                      */
/* ------------------------------------------------------------------ */

export type AeosCoverage = z.infer<typeof coverageSchema>;
export type AeosRunStatus = z.infer<typeof runStatusSchema>;
export type AeosDerivedRunStatus = z.infer<typeof derivedRunStatusSchema>;
export type AeosRunOrigin = z.infer<typeof runOriginSchema>;
export type AeosRunRow = z.infer<typeof runRowSchema>;
export type AeosLogicalSync = z.infer<typeof logicalSyncSchema>;
export type AeosTestTouchedCounters = z.infer<typeof testTouchedCountersSchema>;
export type AeosRunCounts = z.infer<typeof runCountsSchema>;
export type AeosIntegrationRow = z.infer<typeof integrationRowSchema>;
export type AeosEstateReport = z.infer<typeof estateReportSchema>;
export type AeosRunFeed = z.infer<typeof runFeedSchema>;
export type AeosRunEntityRow = z.infer<typeof runEntityRowSchema>;
export type AeosRunEntityPage = z.infer<typeof runEntityPageSchema>;
export type AeosModuleRow = z.infer<typeof moduleRowSchema>;
export type AeosInvoicePolicyView = z.infer<typeof invoicePolicyViewSchema>;
export type AeosAccountPolicyRow = z.infer<typeof accountPolicyRowSchema>;
export type AeosCustomerAccountRow = z.infer<typeof customerAccountRowSchema>;
export type AeosControlPlaneSummary = z.infer<typeof controlPlaneSummarySchema>;
export type AeosEconRollUpRow = z.infer<typeof econRollUpRowSchema>;
export type AeosEconReport = z.infer<typeof econReportSchema>;
export type AeosChangeKind = z.infer<typeof changeKindSchema>;
export type AeosChangeScope = z.infer<typeof changeScopeSchema>;
export type AeosChangeRequestStatus = z.infer<typeof changeStatusSchema>;
export type AeosApprovalReason = z.infer<typeof approvalReasonSchema>;
export type AeosApprovalCapability = z.infer<typeof approvalCapabilitySchema>;
export type AeosBillablePhase = (typeof AEOS_BILLABLE_PHASES)[number];
export type AeosModulePauseChange = z.infer<typeof aeosModulePauseChangeSchema>;
export type AeosInvoicePolicyChange = z.infer<typeof aeosInvoicePolicyChangeSchema>;
export type AeosAccountPolicyChange = z.infer<typeof aeosAccountPolicyChangeSchema>;
export type AeosAccountManagedChange = z.infer<typeof aeosAccountManagedChangeSchema>;
export type AeosDesiredChange = z.infer<typeof desiredChangeVariantSchema>;
export type AeosChangeRequestCreate = z.infer<typeof aeosChangeRequestCreateSchema>;
export type AeosChangeSummary = z.infer<typeof changeSummarySchema>;
export type AeosChangeReview = z.infer<typeof changeReviewSchema>;
export type AeosChangeListResponse = z.infer<typeof changeListResponseSchema>;
export type AeosChangeCreateResponse = z.infer<typeof changeCreateResponseSchema>;
export type AeosChangeApprovalResponse = z.infer<typeof changeApprovalResponseSchema>;

export interface AeosApprovalRequest {
  changeRequestId: string;
  /** Copied from the loaded review snapshot: proves nobody else already acted. */
  expectedLifecycleRevision: number;
  /** Copied from the loaded review snapshot: proves the reviewed bytes are intact. */
  expectedDesiredStateHash: string;
}

/* ------------------------------------------------------------------ */
/* Requests                                                            */
/* ------------------------------------------------------------------ */

function withQuery(path: string, params: Record<string, string | number | undefined>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      query.set(key, String(value));
    }
  }
  const serialized = query.toString();
  return serialized.length > 0 ? `${path}?${serialized}` : path;
}

export async function getAeosEstate(): Promise<AeosEstateReport> {
  const response = await apiFetch<unknown>('/ops/aeos/estate');
  return estateReportSchema.parse(response);
}

export async function getAeosControlPlane(): Promise<AeosControlPlaneSummary> {
  const response = await apiFetch<unknown>('/ops/aeos/control-plane');
  return controlPlaneSummarySchema.parse(response);
}

/**
 * The unit economics AEOS computed.
 *
 * Omit `period` to follow the AEOS latest-period pointer. Peregrine performs no econ
 * calculation of its own, so there is no second figure here that could disagree with AEOS.
 */
export async function getAeosEcon(period?: string): Promise<AeosEconReport> {
  const response = await apiFetch<unknown>(withQuery('/ops/aeos/econ', { period }));
  return econReportSchema.parse(response);
}

/** `status` filters on the status AEOS wrote, never on the derived status. */
export async function listAeosRuns(
  status: AeosRunStatus,
  limit?: number,
): Promise<AeosRunFeed> {
  const response = await apiFetch<unknown>(withQuery('/ops/aeos/runs', { status, limit }));
  return runFeedSchema.parse(response);
}

export async function listAeosRunEntities(
  syncId: string,
  limit?: number,
  cursor?: string,
): Promise<AeosRunEntityPage> {
  const path = withQuery(`/ops/aeos/runs/${encodeURIComponent(syncId)}/entities`, {
    limit,
    cursor,
  });
  const response = await apiFetch<unknown>(path);
  return runEntityPageSchema.parse(response);
}

export async function listAeosChanges(
  limit?: number,
  cursor?: string,
): Promise<AeosChangeListResponse> {
  const response = await apiFetch<unknown>(withQuery('/ops/aeos/changes', { limit, cursor }));
  return changeListResponseSchema.parse(response);
}

export async function getAeosChange(changeRequestId: string): Promise<AeosChangeReview> {
  const response = await apiFetch<unknown>(
    `/ops/aeos/changes/${encodeURIComponent(changeRequestId)}`,
  );
  return changeDetailResponseSchema.parse(response).changeRequest;
}

export async function createAeosChange(
  input: AeosChangeRequestCreate,
): Promise<AeosChangeCreateResponse> {
  const validated = aeosChangeRequestCreateSchema.parse(input);
  const response = await apiFetch<unknown>('/ops/aeos/changes', {
    method: 'POST',
    body: JSON.stringify(validated),
  });
  return changeCreateResponseSchema.parse(response);
}

export async function approveAeosChange(
  request: AeosApprovalRequest,
): Promise<AeosChangeApprovalResponse> {
  const response = await apiFetch<unknown>(
    `/ops/aeos/changes/${encodeURIComponent(request.changeRequestId)}/approve`,
    {
      method: 'POST',
      body: JSON.stringify({
        expectedLifecycleRevision: request.expectedLifecycleRevision,
        expectedDesiredStateHash: request.expectedDesiredStateHash,
      }),
    },
  );
  return changeApprovalResponseSchema.parse(response);
}

/**
 * Whether an approval failed because the record moved under the reviewer.
 *
 * The shared HTTP client surfaces the API's message and discards the status code, so
 * the 409 is recognised by the sentence the backend sends with `STALE_REVISION`. A
 * false negative only costs a less specific message — the caller reloads the detail
 * and clears the confirmation either way, so a stale approval can never be resubmitted.
 */
export function isAeosRevisionConflict(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  return /reload and review the latest revision/i.test(error.message);
}
